// axios 싱글톤 + ApiResponse 자동 언래핑
// access token 은 메모리 보관 (XSS 차단), refresh 는 HttpOnly 쿠키로 자동 처리
import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';

export type ApiEnvelope<T> = {
  success: boolean;
  code: string;
  message: string;
  data: T;
  timestamp: string;
};

/** 서버 envelope 기반 오류 — message 외에 HTTP status 와 업무 code 를 함께 제공. */
export type ApiError = Error & { status?: number; code?: string };

let _accessToken: string | null = null;
let _onAuthFailure: (() => void) | null = null;
let _userTypeGetter: (() => string | null) | null = null;
let _isRefreshing = false;
const _refreshQueue: Array<(token: string | null) => void> = [];

export function registerUserTypeGetter(fn: () => string | null) {
  _userTypeGetter = fn;
}

export function setTokens(token: string | null) {
  _accessToken = token;
}

export function getToken(): string | null {
  return _accessToken;
}

export function clearTokens() {
  _accessToken = null;
}

/** 인증 최종 실패 시 호출할 콜백 등록 (useAuth 에서 로그아웃 처리). */
export function registerAuthFailureHandler(fn: () => void) {
  _onAuthFailure = fn;
}

function processQueue(token: string | null) {
  _refreshQueue.splice(0).forEach(cb => cb(token));
}

const BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/v1';

const client: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: 30_000,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

client.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  if (_accessToken) {
    config.headers = config.headers ?? {};
    (config.headers as Record<string, string>).Authorization = `Bearer ${_accessToken}`;
  }
  return config;
});

client.interceptors.response.use(
  (response: AxiosResponse<ApiEnvelope<unknown>>) => {
    const body = response.data;
    if (body && typeof body === 'object' && 'success' in body) {
      if (body.success) return { ...response, data: body.data } as AxiosResponse;
      return Promise.reject(new Error(`[${body.code}] ${body.message}`));
    }
    return response;
  },
  async (err: AxiosError<ApiEnvelope<unknown>>) => {
    const config = err.config as (AxiosRequestConfig & { _retry?: boolean }) | undefined;
    const url = config?.url ?? '';
    const isRefreshPath = url.includes('/refresh');
    // 로그인/회원가입/전화확인 등 인증 진입점의 401 은 "자격 실패" 이므로
    // 토큰 refresh 재시도 대상이 아님. 재시도 시 refresh 도 실패 → 사용자에게
    // 원본 오류(LOGIN_FAILED 등) 대신 refresh 실패가 노출되는 것을 방지.
    const isAuthEntryPoint =
      url.includes('/login') ||
      url.includes('/signup') ||
      url.includes('/quickstart') ||
      url.includes('/check-phone');

    if (err.response?.status === 401 && !isRefreshPath && !isAuthEntryPoint && config && !config._retry) {
      if (_isRefreshing) {
        return new Promise<AxiosResponse>((resolve, reject) => {
          _refreshQueue.push(token => {
            if (!token) { reject(err); return; }
            const retryConfig = { ...config } as InternalAxiosRequestConfig;
            retryConfig.headers = (retryConfig.headers ?? {}) as typeof retryConfig.headers;
            (retryConfig.headers as Record<string, string>).Authorization = `Bearer ${token}`;
            resolve(client(retryConfig));
          });
        });
      }

      config._retry = true;
      _isRefreshing = true;

      try {
        const userType = _userTypeGetter?.() ?? 'MEMBER';
        const refreshEndpoint =
          userType === 'MEMBER' ? '/account/refresh' :
          userType === 'RATER'  ? '/rater/auth/refresh' :
                                  '/admin/auth/refresh';
        const { data } = await client.post(refreshEndpoint);
        const newToken: string = data.accessToken;
        setTokens(newToken);
        processQueue(newToken);
        const retryConfig = { ...config } as InternalAxiosRequestConfig;
        retryConfig.headers = (retryConfig.headers ?? {}) as typeof retryConfig.headers;
        (retryConfig.headers as Record<string, string>).Authorization = `Bearer ${newToken}`;
        return client(retryConfig);
      } catch {
        clearTokens();
        processQueue(null);
        _onAuthFailure?.();
        return Promise.reject(err);
      } finally {
        _isRefreshing = false;
      }
    }

    const envelope = err.response?.data;
    if (envelope && typeof envelope === 'object' && 'message' in envelope) {
      // status/code 를 실어 보내 호출부가 재시도 불가 오류(예: 409 INSTANCE_CLOSED)를 구분할 수 있게 한다.
      const apiErr = new Error(envelope.message ?? '요청 처리에 실패했습니다.') as ApiError;
      apiErr.status = err.response?.status;
      apiErr.code = envelope.code;
      return Promise.reject(apiErr);
    }
    // 응답 자체가 없는 경우 (CORS 차단·네트워크 단절·타임아웃 등).
    // axios 기본 message ('Network Error' / 'timeout of...') 를 그대로 노출하면
    // 사용자에게 의미 없는 코드로만 보여 문의 대응이 어렵다.
    if (!err.response) {
      const isTimeout = err.code === 'ECONNABORTED';
      return Promise.reject(new Error(
        isTimeout
          ? '서버 응답이 지연되고 있습니다. 잠시 후 다시 시도해 주세요.'
          : '서버에 연결할 수 없습니다. 네트워크 상태를 확인하거나 잠시 후 다시 시도해 주세요.'
      ));
    }
    return Promise.reject(err);
  }
);

export default client;
